import {
  AdminNotification,
  AdminNotificationSeverity,
  AdminNotificationType,
  BILLING_ALERT_TYPES,
  ENTITLEMENT_ALERT_TYPES,
} from '../../types/admin-notification.type'

export type AdminNotificationGroupKey =
  | 'all'
  | 'support'
  | 'feedback'
  | 'system'
  | 'billing'
  | 'growth'

export interface AdminNotificationGroup {
  key: AdminNotificationGroupKey
  label: string
  types: AdminNotificationType[]
}

export const NOTIFICATION_GROUPS: AdminNotificationGroup[] = [
  {key: 'all', label: 'All', types: []},
  {
    key: 'support',
    label: 'Support',
    types: ['support_ticket_created', 'support_ticket_message'],
  },
  {
    key: 'feedback',
    label: 'Feedback',
    types: ['user_feedback_submitted', 'contact_form_submitted'],
  },
  {key: 'system', label: 'System', types: ['cron_failed', 'webhook_failed']},
  {key: 'billing', label: 'Billing', types: [...BILLING_ALERT_TYPES]},
  {
    key: 'growth',
    label: 'Growth',
    types: [...ENTITLEMENT_ALERT_TYPES, 'community_repeat_offender'],
  },
]

export const NOTIFICATION_TYPE_LABELS: Record<AdminNotificationType, string> = {
  support_ticket_created: 'New ticket',
  support_ticket_message: 'Ticket message',
  contact_form_submitted: 'Contact form',
  user_feedback_submitted: 'Feedback',
  cron_failed: 'Cron failure',
  webhook_failed: 'Webhook failure',
  entitlement_mismatch: 'Entitlement mismatch',
  refund_spike: 'Refund spike',
  entitlement_store_sample: 'Store sample drift',
  community_repeat_offender: 'Repeat offender',
  purchase_verify_mismatch: 'Purchase verify mismatch',
  shared_apple_subscription: 'Shared Apple sub',
  shared_google_subscription: 'Shared Google sub',
}

export const getGroup = (key: AdminNotificationGroupKey): AdminNotificationGroup =>
  NOTIFICATION_GROUPS.find((group) => group.key === key) ?? NOTIFICATION_GROUPS[0]

/** The All tab omits the argument rather than sending an empty array. */
export const getGroupTypes = (
  key: AdminNotificationGroupKey
): AdminNotificationType[] | undefined => {
  const {types} = getGroup(key)
  return types.length > 0 ? types : undefined
}

export const isInGroup = (
  notification: AdminNotification,
  key: AdminNotificationGroupKey
): boolean => {
  const {types} = getGroup(key)
  return types.length === 0 || types.includes(notification.type)
}

export const getGroupUnreadCount = (
  key: AdminNotificationGroupKey,
  countsByType: {type: AdminNotificationType; count: number}[],
  total: number
): number => {
  const {types} = getGroup(key)
  if (types.length === 0) return total
  return countsByType
    .filter((entry) => types.includes(entry.type))
    .reduce((sum, entry) => sum + entry.count, 0)
}

/**
 * Cron, webhook and contact-form entries have no screen to open: contact
 * submissions are emailed rather than stored, so they carry no entity.
 */
export const getNotificationLink = (notification: AdminNotification): string | null => {
  const kind = notification.entity?.kind
  const id = notification.entity?.id
  const actorUserId = notification.actor?.userId || notification.meta?.userId

  if (kind === 'supportTicket' && id) {
    return `/apps/all-ticket/ticket/details-page/${id}/ticket`
  }

  if (kind === 'feedback' && id) {
    return `/apps/all-feedbacks/feedbacks/details-page/${id}`
  }

  if (notification.type === 'community_repeat_offender') {
    const userId = id || actorUserId
    if (userId) return `/apps/all-users/users/details-page/${userId}/profile`
  }

  if (kind === 'user' && id) {
    return `/apps/all-users/users/details-page/${id}/profile`
  }

  if (kind === 'subscription' && actorUserId) {
    return `/apps/all-users/users/details-page/${actorUserId}/subscriptions`
  }

  if (
    (notification.type === 'shared_apple_subscription' ||
      notification.type === 'shared_google_subscription' ||
      notification.type === 'purchase_verify_mismatch') &&
    (notification.meta?.winnerUserId || actorUserId)
  ) {
    const userId = notification.meta?.winnerUserId || actorUserId
    return `/apps/all-users/users/details-page/${userId}/subscriptions`
  }

  return null
}

export const getSeverityIconPath = (severity: AdminNotificationSeverity): string => {
  if (severity === 'critical') return '/media/icons/duotune/general/gen044.svg'
  if (severity === 'warning') return '/media/icons/duotune/general/gen045.svg'
  return '/media/icons/duotune/general/gen007.svg'
}

export const getSeverityColor = (severity: AdminNotificationSeverity): string => {
  if (severity === 'critical') return 'danger'
  if (severity === 'warning') return 'warning'
  return 'primary'
}

/**
 * Collapsed entries describe a burst, so the count needs to read naturally
 * rather than repeating the single-event title.
 */
export const getCountSummary = (notification: AdminNotification): string | null => {
  if (!notification.count || notification.count <= 1) return null

  const {count, type, meta} = notification
  const ticket = meta?.supportTicketID

  if (type === 'support_ticket_message') {
    return `${count} new messages${ticket ? ` on ticket ${ticket}` : ''}`
  }
  if (type === 'cron_failed') {
    return `${count} failures${meta?.job ? ` of ${meta.job}` : ''}`
  }
  if (type === 'webhook_failed') {
    const source = [meta?.provider, meta?.operation].filter(Boolean).join(' ')
    return `${count} failures${source ? ` on ${source}` : ''}`
  }
  if (type === 'refund_spike') {
    return `${count} refund spike alerts`
  }
  if (type === 'entitlement_mismatch' || type === 'entitlement_store_sample') {
    return `${count} entitlement alerts`
  }

  return `${count} events`
}

export const getNotificationDetail = (notification: AdminNotification): string | null => {
  const {type, actor, meta} = notification

  if (type === 'contact_form_submitted') {
    return actor?.email ?? null
  }
  if (type === 'cron_failed') {
    return [meta?.job, meta?.errorMessage].filter(Boolean).join(' — ') || null
  }
  if (type === 'webhook_failed') {
    return (
      [meta?.provider, meta?.operation, meta?.errorMessage].filter(Boolean).join(' — ') || null
    )
  }
  if (type === 'purchase_verify_mismatch') {
    return [meta?.platform, meta?.mode, meta?.reason].filter(Boolean).join(' — ') || null
  }
  if (type === 'shared_apple_subscription' || type === 'shared_google_subscription') {
    return (
      [
        meta?.originalTransactionId || (meta?.purchaseToken ? `${meta.purchaseToken.slice(0, 12)}…` : null),
        meta?.winnerUserId ? `winner ${meta.winnerUserId}` : null,
      ]
        .filter(Boolean)
        .join(' — ') || null
    )
  }
  if (type === 'entitlement_store_sample') {
    return `mismatches ${meta?.mismatches ?? 0} / sampled ${meta?.sampled ?? 0}`
  }
  if (type === 'refund_spike') {
    return `refunds ${meta?.refundCount ?? meta?.rowCount ?? '—'} (threshold ${meta?.threshold ?? '—'})`
  }
  if (type === 'entitlement_mismatch') {
    return `affected ${meta?.affectedUsers ?? meta?.rowCount ?? '—'}`
  }

  return actor?.name ?? actor?.email ?? null
}

export const truncateSecret = (value?: string | null, keep = 8): string => {
  if (!value) return '—'
  if (value.length <= keep * 2) return value
  return `${value.slice(0, keep)}…${value.slice(-keep)}`
}
